/**
 * "WhatsApp number" — the user's own staff verification (#3874).
 *
 * A number typed into a profile proves nothing: anyone could type a colleague's.
 * Staff are recognised on the workshop's WhatsApp only after they send a
 * one-time code, from the phone, to the workshop's business number. This section
 * starts that, shows the code and where to send it, and shows what the server
 * says — never what the browser hopes.
 *
 * - **Starting is not verifying.** The status stays "waiting" until the server
 *   has received the message; the section polls and flips by itself.
 * - **The code is shown only while the server says it is pending and unexpired,**
 *   and is held in memory only (see the hook). It is not stored, logged or put
 *   in a URL we control; the "Open WhatsApp" link is the user's own wa.me link.
 * - **Self-service only.** There is no way here to verify someone else.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import { useStaffWhatsAppVerification } from '@/hooks/useStaffWhatsAppVerification';

const STATUS_COLOR = {
  unverified: 'default',
  pending: 'warning',
  verified: 'success',
  revoked: 'default',
} as const;

const TICK_MS = 1000;

/** `358401234567` → `+358401234567`; the digits are already canonical. */
const asInternational = (digits: string): string => `+${digits}`;

export function StaffWhatsAppVerificationSection() {
  const { t, i18n } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const accountKey = user ? `${user.tenantId}:${user.id}` : null;
  const verification = useStaffWhatsAppVerification(accountKey);
  const { state, challenge } = verification;

  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sawChallenge, setSawChallenge] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Countdown while a code is on screen. Recomputed from the deadline each tick.
  useEffect(() => {
    if (!challenge) return;
    setSawChallenge(true);
    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [challenge]);

  // A code the server accepted, or an account switch, ends the "it expired" note.
  useEffect(() => {
    if (state?.status === 'verified') setSawChallenge(false);
  }, [state?.status]);
  useEffect(() => {
    setSawChallenge(false);
    setCopied(false);
    setConfirmRevoke(false);
  }, [accountKey]);

  if (!user) return null;

  const status = state?.status;
  const destination = state?.destination ?? null;
  const expired = sawChallenge && !challenge && status !== 'verified' && !verification.startError;
  const minutesLeft = challenge
    ? Math.max(1, Math.ceil((Date.parse(challenge.expiresAt) - now) / 60_000))
    : 0;

  const copyCode = () => {
    if (!challenge) return;
    navigator.clipboard
      .writeText(challenge.challenge)
      .then(() => {
        setCopied(true);
      })
      .catch(() => {
        // The code is on screen and selectable; copying is a convenience.
      });
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('staffWhatsApp.heading')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('staffWhatsApp.description', { app: tc('appName') })}
        </Typography>

        {verification.isLoading && !state && <CircularProgress size={24} aria-label={tc('loading')} />}

        {verification.loadFailed && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={verification.refresh}>
                {t('staffWhatsApp.refresh')}
              </Button>
            }
          >
            {t('staffWhatsApp.loadError')}
          </Alert>
        )}

        {state && status && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                color={STATUS_COLOR[status]}
                label={t(`staffWhatsApp.status.${status}`)}
              />
              {state.number && (
                <Typography variant="body2">
                  {t('staffWhatsApp.numberLine', { number: asInternational(state.number) })}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t(`staffWhatsApp.${status}Help`, {
                destination: destination ? asInternational(destination.phoneNumber) : '',
                date: state.verifiedAt ? new Date(state.verifiedAt).toLocaleDateString(i18n.language) : '',
              })}
            </Typography>
          </Stack>
        )}

        {state && !destination && status !== 'verified' && (
          <Alert severity="warning">{t('staffWhatsApp.notConfigured')}</Alert>
        )}

        {verification.startError && (
          <Alert severity="error">{t(`staffWhatsApp.errors.${verification.startError}`)}</Alert>
        )}
        {verification.revokeFailed && <Alert severity="error">{t('staffWhatsApp.errors.generic')}</Alert>}
        {expired && <Alert severity="info">{t('staffWhatsApp.expiredNote')}</Alert>}

        {challenge && (
          <Stack
            spacing={1.5}
            sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
            data-testid="staff-whatsapp-challenge"
          >
            <Typography variant="body2">
              {t('staffWhatsApp.sendTo', { destination: asInternational(challenge.destination.phoneNumber) })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('staffWhatsApp.codeLabel')}
            </Typography>
            <Typography
              component="code"
              variant="body1"
              sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all', userSelect: 'all' }}
            >
              {challenge.challenge}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                component={Link}
                href={challenge.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('staffWhatsApp.openWhatsApp')}
              </Button>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyCode}>
                {copied ? t('staffWhatsApp.copied') : t('staffWhatsApp.copyCode')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('staffWhatsApp.expiresIn', { minutes: minutesLeft })} {t('staffWhatsApp.singleUseNote')}
            </Typography>
          </Stack>
        )}

        {state && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {status !== 'verified' && (
              <Button
                variant={status === 'pending' ? 'outlined' : 'contained'}
                onClick={verification.start}
                disabled={verification.isStarting || !destination}
              >
                {verification.isStarting ? (
                  <CircularProgress size={20} />
                ) : status === 'pending' || status === 'revoked' ? (
                  t('staffWhatsApp.restart')
                ) : (
                  t('staffWhatsApp.start')
                )}
              </Button>
            )}
            {status === 'pending' && (
              <Button onClick={verification.refresh}>{t('staffWhatsApp.refresh')}</Button>
            )}
            {(status === 'verified' || status === 'pending') && (
              <Button
                color="error"
                onClick={() => {
                  // A waiting code is simply withdrawn; removing a verified number asks first.
                  if (status === 'pending') verification.revoke();
                  else setConfirmRevoke(true);
                }}
                disabled={verification.isRevoking}
              >
                {status === 'pending' ? tc('cancel') : t('staffWhatsApp.revoke')}
              </Button>
            )}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary">
          {t('staffWhatsApp.changeNote')}
        </Typography>
      </Stack>

      <ConfirmDialog
        open={confirmRevoke}
        title={t('staffWhatsApp.revokeConfirmTitle')}
        message={t('staffWhatsApp.revokeConfirmMessage')}
        confirmText={t('staffWhatsApp.revoke')}
        confirmColor="error"
        loading={verification.isRevoking}
        onConfirm={() => {
          setConfirmRevoke(false);
          verification.revoke();
        }}
        onCancel={() => {
          setConfirmRevoke(false);
        }}
      />
    </Box>
  );
}
